import { useCallback, useMemo } from "react";

import { useI18nLocale } from "@braze/i18n-core";

import type { Permission } from "@lib/permissionConstants";
import { PERMISSION_CONFIGS } from "@lib/permissionConstants";
import sharedConstants from "@lib/shared_constants.json";

import type { RealPermission, RealPermissionValue } from "@services/permissions/types";
import { useFeatureOn } from "@services/useFeatureOn";
import useI18nNamespace from "@services/useI18nNamespace";

/**
 * Capitalized display strings for permissions.
 */
export const usePermissionDisplayString = (
  permissionValueToPermission: (permission: RealPermissionValue) => RealPermission
) => {
  const { t: permissionT } = useI18nNamespace("permission-constants");
  const locale = useI18nLocale();
  const featureOn = useFeatureOn();

  const permissionNames = useMemo(() => {
    return Object.fromEntries(
      PERMISSION_CONFIGS(permissionT, featureOn as (feature: string) => boolean).flatMap((permissionConfig) => {
        if (permissionConfig.permission !== sharedConstants.PERMISSIONS.PERMISSIONS_BY_VALUE["placeholder"]) {
          const displayName =
            "display_name" in permissionConfig
              ? permissionConfig.display_name
              : permissionConfig
                  .dynamic_display_name(featureOn as (featureName: string) => boolean, new Intl.Locale(locale))
                  .toString();
          return [[permissionValueToPermission(permissionConfig.permission), displayName]];
        }

        return [];
      })
    );
  }, [permissionT, featureOn, locale, permissionValueToPermission]);

  return useCallback(
    (permission: Permission) => {
      return permissionNames[permission];
    },
    [permissionNames]
  );
};
